import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { StanInput, StanModal } from '@/components';
import { useTransferAffiliateLink } from '../queries/useAffiliateLinkActions';
import { type TransferLinkFormData, transferLinkSchema } from '../validations/transferLink.validation';
import type { AffiliateLink } from '../types/affiliateLinks.types';

interface TransferLinkModalProps {
    isOpen: boolean;
    onClose: () => void;
    link: AffiliateLink | null;
}

export function TransferLinkModal({ isOpen, onClose, link }: TransferLinkModalProps) {
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<TransferLinkFormData>({
        resolver: zodResolver(transferLinkSchema),
        defaultValues: {
            email: '',
            username: '',
        },
    });

    const { mutate: transferLink, isPending } = useTransferAffiliateLink();

    const onSubmit = (data: TransferLinkFormData) => {
        if (!link) return;

        transferLink(
            {
                linkId: link.id,
                email: data.email,
                username: data.username,
            },
            {
                onSuccess: () => {
                    reset();
                    onClose();
                },
            }
        );
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    return (
        <StanModal
            id='transfer-affiliate-link-modal'
            isOpen={isOpen}
            onClose={handleClose}
            title='Transfer Affiliate Link'
            size='md'
            primaryActionLabel='Transfer Affiliate Link'
            secondaryActionLabel='Cancel'
            onPrimaryAction={handleSubmit(onSubmit)}
            onSecondaryAction={handleClose}
            primaryActionLoading={isPending}
            primaryActionDisabled={isPending}
        >
            <div className='flex flex-col gap-6'>
                <p className='text-gray text-label-sm-regular -mt-2 text-center'>
                    Please provide the email address of the account you&apos;d like to transfer your affiliate link to:
                </p>

                <div className='space-y-2'>
                    <label className='text-label-xs-bold text-dark block'>Email Address</label>
                    <Controller
                        name='email'
                        control={control}
                        render={({ field }) => (
                            <StanInput
                                {...field}
                                placeholder='email@example.com'
                                error={!!errors.email}
                                disabled={isPending}
                            />
                        )}
                    />
                    {errors.email && <p className='text-negative-active text-label-xs-bold'>{errors.email.message}</p>}
                </div>

                <div className='space-y-2'>
                    <label className='text-label-xs-bold text-dark block'>Stan Username</label>
                    <Controller
                        name='username'
                        control={control}
                        render={({ field }) => (
                            <StanInput
                                {...field}
                                placeholder='@username'
                                error={!!errors.username}
                                disabled={isPending}
                            />
                        )}
                    />
                    {errors.username && (
                        <p className='text-negative-active text-label-xs-bold'>{errors.username.message}</p>
                    )}
                </div>
            </div>
        </StanModal>
    );
}
